import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAppointment } from '../hooks/useAppointment';
import { useAvailabilities } from '../hooks/useAvailabilities';
import { useBookingForm } from '../hooks/useBookingForm';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { useScene } from '../hooks/useScene';
import { useTimezone } from '../hooks/useTimezone';
import { Appointment, FormFieldValues, SubmissionError, TimeSlotEntry, TimezoneOption } from '../types/booking';
import { ApiData, FormField } from '../types/rest';

interface BookingContextValue {
	completeTask: () => void;
	settings: ApiData['settings'];
	setScene: (id: string) => void;
	locale: string;
	service: {
		businessId: string;
		serviceId: string;
	};
	timezone: {
		current: string;
		options: TimezoneOption[];
		set: (timezone: string) => void;
	};
	appointment: {
		abortCancellation: () => void;
		canBeEdited: boolean;
		cancel: () => Promise<boolean>;
		clear: () => void;
		data: Appointment | null;
		edit: () => void;
		isEditing: boolean;
		isCancelling: boolean;
		isSubmitting: boolean;
		pendingCancellation: boolean;
		save: () => void;
		setTimeSlot: (timeSlot: TimeSlotEntry | null) => void;
		source: 'stored' | 'created' | null;
		submissionError: SubmissionError | null;
		submit: (formFields: FormFieldValues) => Promise<{ success: boolean; fieldErrors?: Record<string, string> }>;
		timeSlot: TimeSlotEntry | null;
		timeSlotExpired: boolean;
	};
	availability: {
		options: TimeSlotEntry[];
		isLoading: boolean;
		fetch: () => void;
	};
	form: {
		fields: FormField[];
		groupedFields: FormField[][];
		nextStep: () => void;
		previousStep: () => void;
		step: number;
		submit: () => Promise<{ success: boolean; fieldErrors?: Record<string, string> }>;
		totalSteps: number;
		updateFieldValue: (name: string, value: string | string[] | boolean) => void;
	};
}

const BookingContext = createContext<BookingContextValue | undefined>(undefined);

interface BookingProviderProps {
	children: ReactNode;
	completeTask: () => void;
	data: ApiData;
	setSceneDataById: (id: number) => void;
}

export function BookingProvider({ children, completeTask, data, setSceneDataById }: BookingProviderProps) {
	const { booking_business_id: bookingBusinessId, booking_service_id: bookingServiceId } = data.booking_configuration;
	const { gtmInitializeBookingData, gtmEventBookAppointment } = useAnalytics(bookingBusinessId, bookingServiceId);
	const { generateRecaptchaToken } = useRecaptcha(data.recaptcha_site_key);
	const { timezone, timezoneOptions, setTimezone } = useTimezone(data.timezones, data.user_country, data.settings.fallback_timezone);
	const {
		availabilities,
		fetchAvailabilities,
		isLoading: isLoadingAvailabilities,
	} = useAvailabilities({
		bookingBusinessId,
		bookingServiceId,
		data,
		timezone,
	});

	const { setScene } = useScene(data, setSceneDataById);

	const {
		abortCancellation,
		appointment,
		appointmentSource,
		cancelAppointment,
		canEditAppointment,
		clearAppointment,
		editAppointment,
		isCancelling,
		isEditing,
		isSubmitting,
		pendingCancellation,
		saveAppointment,
		setTimeSlot,
		submissionError,
		submitAppointment,
		timeSlot,
		timeSlotExpired,
	} = useAppointment({
		availabilities,
		bookingBusinessId,
		bookingServiceId,
		cancelAppointmentUrl: data.rest_routes.cancel_appointment,
		fetchAvailabilities,
		generateRecaptchaToken,
		getAppointmentUrl: data.rest_routes.get_appointment,
		settings: data.settings,
		submitAppointmentUrl: data.rest_routes.submit_appointment,
		timezone,
	});

	const { fields, groupedFields, nextStep, previousStep, step, submitForm, totalSteps, updateFieldValue } = useBookingForm({
		formFieldData: data.booking_configuration.form_fields,
		gtmEventBookAppointment,
		submitAppointment,
	});

	useEffect(() => {
		gtmInitializeBookingData(totalSteps);
	}, []);

	return (
		<BookingContext.Provider
			value={{
				completeTask,
				setScene,
				settings: data.settings,
				locale: navigator.language || data.settings.fallback_locale,
				service: {
					businessId: bookingBusinessId,
					serviceId: bookingServiceId,
				},
				timezone: {
					current: timezone,
					options: timezoneOptions,
					set: setTimezone,
				},
				appointment: {
					abortCancellation,
					canBeEdited: canEditAppointment,
					cancel: cancelAppointment,
					clear: clearAppointment,
					data: appointment,
					isEditing,
					isCancelling,
					pendingCancellation,
					timeSlot,
					timeSlotExpired,
					edit: editAppointment,
					save: saveAppointment,
					setTimeSlot,
					source: appointmentSource,
					submit: submitAppointment,
					isSubmitting,
					submissionError,
				},
				availability: {
					options: availabilities,
					isLoading: isLoadingAvailabilities,
					fetch: fetchAvailabilities,
				},
				form: {
					fields,
					groupedFields,
					nextStep,
					previousStep,
					step,
					submit: submitForm,
					totalSteps,
					updateFieldValue,
				},
			}}
		>
			{children}
		</BookingContext.Provider>
	);
}

export function useBookingContext() {
	const context = useContext(BookingContext);
	if (context === undefined) {
		throw new Error('useBookingContext must be used within a BookingProvider');
	}
	return context;
}
